import { AfterViewInit, Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import * as L from 'leaflet';
import 'leaflet-editable';
import 'leaflet.gridlayer.googlemutant';
import 'leaflet-geometryutil';
import { Route, Segment } from '../../../models/segment';
import { timer, from, of } from 'rxjs';
import { ConfirmSentComponent } from '../confirm-sent/confirm-sent.component';
import { AppToastService } from '../../../services/toast.service';
import { catchError } from 'rxjs/operators';
import { Accessibility } from '../../management/manage-map/registry';
import { AppConfig } from 'src/app/app.config';
import { UserService } from 'src/app/services/user.service';
import _ from 'lodash';

declare module 'leaflet' {
  export class GeometryUtil {
    static computeAngle(a: L.Point, b: L.Point): number;
    static closest(map: L.Map, layer: L.Layer, latlng: L.LatLng, vertices?: boolean): L.LatLng;
    static distance(map, latlngA, latlngB): Number;
    static belongsSegment(latlng: L.LatLng, latlngA: L.LatLng, latlngB: L.LatLng): boolean;
  }
}

type TSegment = {
  color?: Accessibility;
  markers?: [L.Marker?, L.Marker?];
  polyline?: L.Polyline;
  description?: string;
  from?: number;
  to?: number;
};

@Component({
  selector: 'app-trip-uploader',
  templateUrl: './trip-uploader.component.html',
  styleUrls: ['./trip-uploader.component.scss'],
})
export class TripUploaderComponent implements OnInit, AfterViewInit {
  projectId: number;

  latlngs: L.LatLng[] = [];
  basePolyline: L.Polyline;
  segments: TSegment[] = [
    {
      markers: [],
    },
  ];
  unsafes: TSegment[] = [];

  step: number = 0;
  color: 'UNSAFE' | 'OCCASIONALLY_UNSAFE' = 'UNSAFE';

  map: L.DrawMap;
  mapOptions: L.MapOptions;
  mapTiles = [];
  mapType = 1;

  MIN_DISTANCE = 5;

  colors = {
    SAFE: '#8cbf70',
    OCCASIONALLY_UNSAFE: '#e8a453',
    UNSAFE: '#ff0000',
  };

  markerIcons = {
    OCCASIONALLY_UNSAFE: new L.Icon({
      iconUrl: 'https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    UNSAFE: new L.Icon({
      iconUrl: 'https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
    SELECT: new L.Icon({
      iconUrl: 'https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    }),
  };

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private appConfig: AppConfig,
    private userService: UserService,
    private appToastService: AppToastService,
  ) {}

  ngOnInit() {
    this.projectId = this.userService.project.projectID.id;
  }

  ngAfterViewInit(): void {
    this.mapTiles = [
      // @ts-ignore
      L.gridLayer.googleMutant({ type: 'satellite' }),
      this.appConfig.getTileLayer(),
    ];

    this.mapOptions = {
      layers: [this.mapTiles[this.mapType]],
    };

    this.map = L.map('map-popup', this.mapOptions);

    timer(0).subscribe(() => {
      this.map.fitBounds(L.latLngBounds(this.latlngs));
      this.initBasePolyline(this.latlngs);
    });
  }

  toggleMapType() {
    this.mapTiles[this.mapType].removeFrom(this.map);
    this.mapType = (this.mapType + 1) % this.mapTiles.length;
    this.mapTiles[this.mapType].addTo(this.map);
  }

  updateSegment(segmentIndex: number) {
    if (segmentIndex > this.segments.length - 1) {
      return;
    }

    const segment = this.segments[segmentIndex];

    if (segment.polyline) {
      segment.polyline.removeFrom(this.map);
    }

    if (segment.markers.length === 2) {
      const { latlngs, from, to } = this.getSegmentLatlngData(segmentIndex);
      segment.polyline = this.generatePolyline(latlngs, segment.color).addTo(this.map);
      segment.from = from;
      segment.to = to;
    }
  }

  getSegmentLatlngData(segmentIndex: number) {
    if (segmentIndex > this.segments.length - 1) {
      return {
        latlngs: [],
        from: -1,
        to: -1,
      };
    }

    const source = this.basePolyline.getLatLngs() as L.LatLng[];
    const segment = this.segments[segmentIndex];

    if (source.length === 0 || segment.markers.length === 0) {
      return {
        latlngs: [],
        from: -1,
        to: -1,
      };
    }

    const fromLatlng = segment.markers[0].getLatLng();
    let toLatlng = fromLatlng;

    if (segment.markers.length === 2) {
      toLatlng = segment.markers[1].getLatLng();
    }

    let idxF = 0;
    let idxT = 0;
    let minDF = fromLatlng.distanceTo(source[idxF]);
    let minDT = toLatlng.distanceTo(source[idxT]);

    source.forEach((latlng, index) => {
      let distance = fromLatlng.distanceTo(latlng);
      if (minDF > distance) {
        minDF = distance;
        idxF = index;
      }

      distance = toLatlng.distanceTo(latlng);
      if (minDT > distance) {
        minDT = distance;
        idxT = index;
      }
    });

    let latlngs: L.LatLng[] = [];
    if (idxF < idxT) {
      latlngs.push(fromLatlng);
      latlngs = latlngs.concat(source.slice(idxF + 1, idxT));
      latlngs.push(toLatlng);
    } else if (idxF > idxT) {
      latlngs.push(toLatlng);
      latlngs = latlngs.concat(source.slice(idxT + 1, idxF));
      latlngs.push(fromLatlng);
    } else {
      latlngs = [fromLatlng, toLatlng];
    }

    return {
      latlngs,
      from: Math.min(idxF, idxT) + 1,
      to: Math.max(idxF, idxT) - 1,
    };
  }

  generatePolyline(latlngs: L.LatLng[], accessibility: Accessibility) {
    return L.polyline(latlngs, {
      color: this.colors[accessibility],
      weight: 5,
    });
  }

  getSegmentBounds(segmentIndex: number) {
    if (segmentIndex > this.segments.length - 1) {
      return [];
    }

    const segment = this.segments[segmentIndex];
    let latlngs: L.LatLng[];
    if (segment.markers.length === 2) {
      latlngs = segment.polyline.getLatLngs() as L.LatLng[];
    } else if (segment.markers.length === 1) {
      latlngs = [segment.markers[0].getLatLng(), segment.markers[0].getLatLng()];
    } else {
      return [];
    }

    const bounds: L.LatLngBounds[] = [];
    for (let i = 0; i < latlngs.length - 1; i++) {
      bounds.push(L.latLngBounds(latlngs[i], latlngs[i + 1]));
    }

    return bounds;
  }

  checkOverlapping(segmentIndex: number) {
    if (segmentIndex > this.segments.length - 1) {
      return false;
    }

    const targetSegment = this.segments[segmentIndex];
    const targetBounds = this.getSegmentBounds(segmentIndex);

    return this.segments.some((segment, index) => {
      if (index === segmentIndex) {
        return false;
      }

      const bounds = this.getSegmentBounds(index);
      return targetBounds.some((bound) => bounds.some((b) => b.intersects(bound)));
    });
  }

  getSegmentDistance(segment: TSegment) {
    if (segment.polyline || segment.markers.length === 2) {
      let distance = 0;
      (segment.polyline.getLatLngs() as L.LatLng[]).forEach((latlng, index, latlngs) => {
        if (index + 1 < latlngs.length) {
          distance += latlng.distanceTo(latlngs[index + 1]);
        }
      });
      return distance;
    }

    return Number.MAX_VALUE;
  }

  initBasePolyline(latlngs: L.LatLng[]) {
    this.basePolyline = this.generatePolyline(latlngs, 'SAFE');

    this.basePolyline.on('click', (e: L.LeafletMouseEvent) => {
      if (this.step > 0) {
        return;
      }

      const marker = L.marker(e.latlng, {
        icon: this.markerIcons[this.color],
        draggable: true,
      });

      marker.on('dragstart', (e) => {
        const target = e.target as L.Marker;
        target['oldLatlng'] = target.getLatLng();
      });

      marker.on('drag', (e) => {
        const target = e.target as L.Marker;
        const closestLatlng = this.findClosest(this.basePolyline, target.getLatLng());
        target.setLatLng(closestLatlng);
        this.updateSegment(target['segmentIndex']);
      });

      marker.on('dragend', (e: L.DragEndEvent) => {
        const target = e.target as L.Marker;
        const targetIndex = target['segmentIndex'];
        this.updateSegment(targetIndex);

        if (this.getSegmentDistance(this.segments[targetIndex]) < this.MIN_DISTANCE) {
          target.setLatLng(target['oldLatlng']);
          this.updateSegment(targetIndex);
          return;
        }

        if (this.checkOverlapping(targetIndex)) {
          this.showOverlappingNotification();
          target.setLatLng(target['oldLatlng']);
          this.updateSegment(targetIndex);
          return;
        }
      });

      const targetIndex = this.segments.findIndex(
        (segment) => segment.markers.length === 1 && segment.color === this.color,
      );
      const lastIndex = this.segments.length - 1;
      const finalIndex = targetIndex === -1 ? lastIndex : targetIndex;

      this.segments[finalIndex].color = this.color;
      this.segments[finalIndex].markers.push(marker);
      marker['segmentIndex'] = finalIndex;
      this.updateSegment(finalIndex);

      if (this.getSegmentDistance(this.segments[finalIndex]) < this.MIN_DISTANCE) {
        this.segments[finalIndex].markers.pop();
        this.updateSegment(finalIndex);
        return;
      }

      if (this.checkOverlapping(finalIndex)) {
        this.showOverlappingNotification();
        this.segments[finalIndex].markers.pop();
        this.updateSegment(finalIndex);
        return;
      }

      marker.addTo(this.map);
      this.updateSegment(finalIndex);

      if (this.segments[lastIndex].markers.length !== 0) {
        this.segments.push({ markers: [] });
      }
    });

    this.basePolyline.addTo(this.map);
  }

  findClosest(polyline: L.Polyline, latlng: L.LatLng) {
    return L.GeometryUtil.closest(this.map, polyline, latlng);
  }

  showOverlappingNotification() {
    this.appToastService.show(
      'To avoid overlap between segments, it is not permitted to place a marker at this location.',
      {
        delay: 3000,
        classname: 'bg-danger text-white',
      },
    );
  }

  resetSegmentsStyle() {
    this.unsafes.forEach((segment) => {
      segment.markers.forEach((marker) => {
        marker.setIcon(this.markerIcons[segment.color]);
      });

      segment.polyline.setStyle({ weight: 5, lineCap: 'round', dashArray: null });
    });
  }

  activeSegment(segmentIndex: number) {
    if (segmentIndex > this.unsafes.length - 1) {
      return;
    }

    this.resetSegmentsStyle();

    const segment = this.unsafes[segmentIndex];

    segment.markers.forEach((marker) => {
      marker.setIcon(this.markerIcons['SELECT']);
    });

    segment.polyline.setStyle({
      weight: 3,
      lineCap: 'butt',
      dashArray: '3, 1',
    });
  }

  prev() {
    this.step--;
    if (this.step === -1) {
      this.step = 0;
    }

    if (this.step === 0) {
      this.resetSegmentsStyle();
      this.resetUnsafe();
    }

    if (this.step > 0) {
      this.activeSegment(this.step - 1);
    }
  }

  next() {
    this.step++;

    if (this.step === 1) {
      this.initUnsafe();
    }

    if (this.step > 0) {
      this.activeSegment(this.step - 1);
    }

    if (this.step > this.unsafes.length) {
      this.step--;
      this.saveTrip();
    }
  }

  resetUnsafe() {
    this.segments.forEach((segment) => {
      if (segment.markers.length === 1) {
        segment.markers[0].addTo(this.map);
      }
    });

    this.unsafes = [];
  }

  initUnsafe() {
    this.segments.forEach((segment) => {
      if (segment.markers.length === 1) {
        segment.markers[0].removeFrom(this.map);
      }
    });

    this.unsafes = this.segments.filter((segment) => segment.markers.length === 2);
  }

  saveTrip() {
    from(this.modalService.open(ConfirmSentComponent).result)
      .pipe(catchError((err) => of('no')))
      .subscribe((result) => {
        if (result !== 'no') {
          this.activeModal.close(this.generateResult());
          return;
        }

        if (this.step === 0) {
          this.resetUnsafe();
        }
      });
  }

  get actionLabel() {
    return this.step === this.unsafes.length ? 'Send' : 'Next';
  }

  generateResult() {
    const result = new Route();

    result.projectId = this.projectId;
    let segments: TSegment[] = [];
    segments.push({
      color: 'SAFE',
      polyline: this.basePolyline,
      from: 0,
      to: this.basePolyline.getLatLngs().length - 1,
    });

    this.unsafes.forEach((segment) => {
      segments = this.divideSegment(segments, segment);
    });

    segments = segments.filter((segment) => this.getSegmentDistance(segment) > this.MIN_DISTANCE);

    result.segments = segments.map((segment) => {
      const newSegment = new Segment(segment.polyline, segment.color);
      newSegment.description = segment.description;
      return newSegment;
    });

    return result;
  }

  divideSegment(segments: TSegment[], sourceSegment: TSegment) {
    const latlngs = this.basePolyline.getLatLngs() as L.LatLng[];
    const targetIndex = segments.findIndex(
      (segment) => sourceSegment.from >= segment.from && sourceSegment.to <= segment.to,
    );

    if (targetIndex === -1) {
      return segments;
    }

    const targetSegment = segments[targetIndex];
    const targetLatlngs = targetSegment.polyline.getLatLngs() as L.LatLng[];
    const sourceLatlngs = sourceSegment.polyline.getLatLngs() as L.LatLng[];

    const newSegments: TSegment[] = [];
    newSegments.push({
      polyline: L.polyline([
        targetLatlngs[0],
        ...latlngs.slice(targetSegment.from, sourceSegment.from),
        sourceLatlngs[0],
      ]),
      color: 'SAFE',
      description: '',
      from: targetSegment.from,
      to: sourceSegment.from,
    });
    newSegments.push(sourceSegment);
    newSegments.push({
      polyline: L.polyline([
        sourceLatlngs[sourceLatlngs.length - 1],
        ...latlngs.slice(sourceSegment.to, targetSegment.to),
        targetLatlngs[targetLatlngs.length - 1],
      ]),
      color: 'SAFE',
      description: '',
      from: sourceSegment.to,
      to: targetSegment.to,
    });

    return [...segments.slice(0, targetIndex), ...newSegments, ...segments.slice(targetIndex + 1)];
  }
}
